// pages/SignageBrokerageUpsertAgentProduct.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { messageHandler } from "utils/messageHandler";
import { AGENT_MESSAGES, AGENTOFFICEPRODUCTADDEDIT_MESSAGES } from "utils/messages";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";

import AgentProductForm from "components/AgentProductForm";
import { getAuthCookie } from "../../utils/authCookies";
import { resolveSignageSessionOrganizationId } from "../../api/salesOrderApi";

export type SelectOption = { value: string; label: string };

// Select2 types for agents
type AgentOption = { 
    value: string; 
    label: string; 
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
};

type Brand = { id: string; name: string; image: string; status: string };
type Category = { id: string; name: string; image: string; status: string };

const PAGE_SIZE = 10;

type Agent = {
    id: string;
    companyID: string;
    companyName: string;
    firstName: string;
    lastName: string;
    status: string;
};

type ProductAPI = {
    name: string;
    sku?: string;
    description?: string;
    brand?: string;
    category?: string;
    item_condition?: string;
    purchase_price?: number | string;
    retail_price?: number | string;
    package_dimensions?: {
        length?: number | string;
        height?: number | string;
        width?: number | string;
        weight?: number | string;
    };
    variants?: Array<{
        inventory?: Array<{ on_hand?: number; available?: number; reserved?: number }>;
    }>;
    threshold_qty?: number | string;
    image?: { url?: string };
    status?: string;
    notes?: string;
};

type AgentInventoryAPI = {
    id: string;
    agent_id: string;
    inventory_type: string;
    product: ProductAPI;
};

type FormValues = {
    agentId: string;
    agentName?: string;
    agentFirstName?: string;
    agentLastName?: string;
    name: string;
    sku: string;
    qty: number | string;
    description: string;
    brand: string;
    category: string;
    item_condition: string;
    purchase_price: string;
    retail_price: string;
    length: string;
    height: string;
    width: string;
    weight: string;
    threshold_qty: string;
    notes: string;
    image: File | null;
    // existing image (edit)
    simageUrl: string;
    statuss: string;
};

const STATUS_OPTIONS: SelectOption[] = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
];

const CONDITION_OPTIONS: SelectOption[] = [
    { value: "new", label: "New" },
    { value: "used", label: "Used" },
    { value: "damaged", label: "Damaged" },
];

export default function SignageBrokerageAddEditAgentProduct() {
    const { firmId, agentId: routeAgentId, productId } = useParams<{
        firmId: string;
        agentId?: string;
        productId?: string;
    }>();
    const isEdit = Boolean(routeAgentId && productId);
    const navigate = useNavigate();
    const token = getAuthCookie("sessionToken") || "";
    const userId = getAuthCookie("userId") || "";

    const effectiveSignageOrgId = resolveSignageSessionOrganizationId() || userId || "";

    // data
    const [agents, setAgents] = useState<Agent[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [agentItem, setAgentItem] = useState<AgentInventoryAPI | null>(null);

    // files
    const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>(
        []
    );
    // was: const [uploadedFileMeta, setUploadedFileMeta] = useState<{ url: string }[]>([]);
    const [uploadedFileMeta, setUploadedFileMeta] = useState<{ alt_text: string; url: string }[]>([]);
    // was: setUploadedFileMeta(existingUrl ? [{ url: existingUrl }] : []);

    // ===== Step 1: Normalize ID once when building Agent options =====
    const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

    const mapAgentToOption = (row: any) => ({
        value: normalizeId(row), // ALWAYS store the id in value
        label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
        firstName: row?.firstName || "",
        lastName: row?.lastName || "",
        email: row?.email || "",
        phone: row?.phone1 || "",
    });

    // ===== Agent Loader (Select2 style) =====
    const loadAgentOptions: LoadOptions<AgentOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;
            console.log("AgentLoader firing with:", { page, inputValue });

            try {
                // Build URL with pagination and search
                const params = new URLSearchParams({
                    licenseType: "BROKERAGE_AGENT",
                    brokerageFirmID: firmId || "",
                    page: String(page),
                    limit: String(PAGE_SIZE)
                });
                const search = (inputValue || "").trim();
                if (search) params.set("search", search);

                const url = `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`;
                console.log('Loading agent options:', { page, search, dynamicSignageId: effectiveSignageOrgId || firmId, url });

                const response = await fetch(url, {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    console.error("Failed to fetch agents:", response.statusText);
                    return { options: [], hasMore: false, additional: { page } };
                }

                const responseData = await response.json();
                console.log('Agent API response:', { page, search, responseData });

                // Handle different response formats
                const results = Array.isArray(responseData) ? responseData : responseData.results || [];
                const hasMore = Array.isArray(responseData)
                    ? results.length === PAGE_SIZE
                    : responseData.pagination?.more || false;

                // Map to react-select options using normalized ID
                const options: AgentOption[] = results.map(mapAgentToOption);

                // Also set the agents for the existing logic
                setAgents(results);

                return {
                    options,
                    hasMore,
                    additional: { page: page + 1 },
                };
            } catch (error) {
                console.error("Error loading agent options:", error);
                return { options: [], hasMore: false, additional: { page } };
            }
        },
        [firmId, effectiveSignageOrgId, token]
    );

    useEffect(() => {
        (async () => {
            try {
                const [b, c] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_URL}/brands`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                    fetch(`${process.env.REACT_APP_API_URL}/categories`, {
                        headers: { Authorization: `Bearer ${token}` },
                    }),
                ]);
                if (b.ok) setBrands(await b.json());
                if (c.ok) setCategories(await c.json());
            } catch { }
        })();
    }, [token]);

    // ---------- fetch existing agent product (edit) ----------
    useEffect(() => {
        if (!isEdit || !routeAgentId || !productId) return;
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/agent/${routeAgentId}/inventory/${productId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!res.ok) throw new Error("Failed to load product");
                const data = (await res.json()) as AgentInventoryAPI;
                setAgentItem(data);
                const existingUrl = data?.product?.image?.url || "";
                setUploadedFileMeta(
                    existingUrl ? [{ url: existingUrl, alt_text: "Product image" }] : []
                );

            } catch (e: any) {
                messageHandler.product.error("fetch", e);
            }
        })();
    }, [isEdit, routeAgentId, productId, token]);

    // ---------- options ----------
    // Remove the old agentOptions array since we're using AsyncPaginate now
    // const agentOptions: SelectOption[] = useMemo(
    //     () =>
    //         (agents || []).map((a) => ({
    //             value: a.id,
    //             label: a.firstName ? `${a.firstName}` : a.companyName || "Agent",
    //         })),
    //     [agents]
    // );
    const brandOptions: SelectOption[] = useMemo(
        () => (brands || []).map((b) => ({ value: b.name, label: b.name })),
        [brands]
    );
    const categoryOptions: SelectOption[] = useMemo(
        () => (categories || []).map((c) => ({ value: c.name, label: c.name })),
        [categories]
    );

    // ---------- initial values ----------
    const initialValues: FormValues = useMemo(
        () => ({
            agentId: agentItem?.agent_id || routeAgentId || "",
            agentName: "", // Will be set by AsyncPaginate
            agentFirstName: "", // Will be set by AsyncPaginate
            agentLastName: "", // Will be set by AsyncPaginate
            name: agentItem?.product?.name || "",
            sku: agentItem?.product?.sku || "",
            qty: agentItem?.product?.variants?.[0]?.inventory?.[0]?.available ?? "",
            description: agentItem?.product?.description || "",
            brand: agentItem?.product?.brand || "",
            category: agentItem?.product?.category || "",
            item_condition: agentItem?.product?.item_condition || "",
            purchase_price: String(agentItem?.product?.purchase_price ?? ""),
            retail_price: String(agentItem?.product?.retail_price ?? ""),
            length: String(agentItem?.product?.package_dimensions?.length ?? ""),
            height: String(agentItem?.product?.package_dimensions?.height ?? ""),
            width: String(agentItem?.product?.package_dimensions?.width ?? ""),
            weight: String(agentItem?.product?.package_dimensions?.weight ?? ""),
            threshold_qty: String(agentItem?.product?.threshold_qty ?? ""),
            notes: agentItem?.product?.notes || "",
            image: null,
            simageUrl: agentItem?.product?.image?.url || "",
            statuss: agentItem?.product?.status || "",
        }),
        [agentItem, routeAgentId]
    );

    // ---------- helpers ----------
    const uploadFile = async (file: File): Promise<string> => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("uploadDir", "brands"); // same as your current add/edit
        const res = await fetch(`${process.env.REACT_APP_API_URL}/images/upload`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
        });
        const json = await res.json();
        if (!res.ok || json?.error) throw new Error(json?.error || "Upload failed");
        return json.path as string;
    };

    const onAddFiles = (files: File[]) => {
        const next = files.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
        setPreviewFiles((p) => [...p, ...next]);
        // also keep formik.image in sync with first file
        if (files[0]) formik.setFieldValue("image", files[0]);
    };

    const onRemovePreview = (index: number) => {
        setPreviewFiles((prev) => {
            const copy = [...prev];
            const [removed] = copy.splice(index, 1);
            if (removed?.url) URL.revokeObjectURL(removed.url);
            return copy;
        });
        if (index === 0) formik.setFieldValue("image", null);
    };

    const onRemoveUploaded = (index: number) => {
        setUploadedFileMeta((prev) => {
            const copy = [...prev];
            copy.splice(index, 1);
            return copy;
        });
    };

    // ---------- formik ----------
    const formik = useFormik<FormValues>({
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.object({
            name: Yup.string().min(2).max(100).required("Product name is required"),
            statuss: Yup.string().required("Status is required"),
            sku: Yup.string().notRequired(),
            qty: Yup.number()
                .typeError("Quantity must be a number")
                .required("Quantity is required")
                .min(1, "Quantity must be at least 1"),
            image: Yup.mixed().test("img-required-on-add", "Image is required", function (value) {
                if (isEdit) return true;
                // on add, require either a selected file or we consider previewFiles/ uploaded present
                return Boolean(value || previewFiles[0]);
            }),
        }),
        onSubmit: async (values, helpers) => {
            try {
                // upload if new image provided
                let imageUrl = values.simageUrl || "";
                if (values.image) {
                    imageUrl = await uploadFile(values.image);
                }

                if (!isEdit) {
                    // ADD: POST /agent/{agentId}/inventory (keeps your original payload) :contentReference[oaicite:2]{index=2}
                    const res = await fetch(
                        `${process.env.REACT_APP_API_URL}/agent/${values.agentId}/inventory`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                agent_id: values.agentId,
                                inventory_type: "agent",
                                product: {
                                    name: values.name,
                                    sku: values.sku,
                                    description: values.description,
                                    brand: values.brand,
                                    category: values.category,
                                    item_condition: values.item_condition,
                                    purchase_price: 1,
                                    retail_price: 1,
                                    package_dimensions: {
                                        length: Number(values.length || 0),
                                        height: Number(values.height || 0),
                                        width: Number(values.width || 0),
                                        weight: Number(values.weight || 0),
                                    },
                                    variants: [
                                        {
                                            variant_name: values.name,
                                            sku: values.sku,
                                            description: values.description,
                                            package_dimensions: {
                                                length: Number(values.length || 0),
                                                height: Number(values.height || 0),
                                                width: Number(values.width || 0),
                                                weight: Number(values.weight || 0),
                                            },
                                            threshold_qty: Number(values.threshold_qty || 0),
                                            inventory: [
                                                {
                                                    on_hand: Number(values.qty || 0),
                                                    available: Number(values.qty || 0),
                                                    reserved: 0,
                                                },
                                            ],
                                            barcode: "",
                                            is_rotational: false,
                                        },
                                    ],
                                    threshold_qty: Number(values.threshold_qty || 0),
                                    image: imageUrl ? { url: imageUrl } : undefined,
                                    status: values.statuss,
                                    is_rotational: false,
                                    linked_mode: "none",
                                    locked_status: "unlocked",
                                    linked_installer: null,
                                },
                            }),
                        }
                    );

                    const json = await res.json();
                    if (!res.ok || json?.error) {
                      messageHandler.product.error("create", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.create.error));
                      throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.create.error);
                    }
                    messageHandler.product.success("create");
                } else {
                    // EDIT: PUT /agent/{agentId}/inventory/{productId} (keeps your original edit payload) :contentReference[oaicite:3]{index=3}
                    const res = await fetch(
                        `${process.env.REACT_APP_API_URL}/agent/${values.agentId || routeAgentId}/inventory/${productId}`,
                        {
                            method: "PUT",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                name: values.name,
                                sku: values.sku,
                                description: values.description,
                                brand: values.brand,
                                category: values.category,
                                item_condition: values.item_condition,
                                purchase_price: Number(values.purchase_price || 0),
                                retail_price: Number(values.retail_price || 0),
                                package_dimensions: {
                                    length: Number(values.length || 0),
                                    height: Number(values.height || 0),
                                    width: Number(values.width || 0),
                                    weight: Number(values.weight || 0),
                                },
                                threshold_qty: Number(values.threshold_qty || 0),
                                status: values.statuss,
                                is_rotational: false,
                                linked_mode: "none",
                                ...(imageUrl ? { image: { url: imageUrl } } : {}),
                            }),
                        }
                    );

                    const json = await res.json();
                    if (!res.ok || json?.error) {
                      messageHandler.product.error("update", new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.update.error));
                      throw new Error(AGENTOFFICEPRODUCTADDEDIT_MESSAGES.update.error);
                    }
                    messageHandler.product.success("update");
                }

                navigate(`/signage/brokerageFirm/${firmId}/#agentInventory`, {
                    state: {
                        successMessage: isEdit
                            ? "Agent Inventory Product updated successfully!"
                            : "Agent Inventory Product created successfully!",
                    },
                });
            } catch (e: any) {
                messageHandler.product.error(isEdit ? "update" : "create", e);
                helpers.setSubmitting(false);
                return;
            }
        },
    });

    // ---------- UI (exact return you requested) ----------
    return (
        <AgentProductForm
            isEdit={isEdit}
            formik={formik as any}
            agentOptions={[]} // Empty array since we're using AsyncPaginate
            loadAgentOptions={loadAgentOptions}
            brandOptions={brandOptions}
            categoryOptions={categoryOptions}
            conditionOptions={CONDITION_OPTIONS}
            statusOptions={STATUS_OPTIONS}
            previewFiles={previewFiles}
            uploadedFiles={uploadedFileMeta}
            onAddFiles={onAddFiles}
            onRemovePreview={onRemovePreview}
            onRemoveUploaded={onRemoveUploaded}
            title={isEdit ? "Edit Product" : "Add Product"}
            onBack={() => navigate(-1)}
            onSubmit={() => formik.handleSubmit()}
            submitting={formik.isSubmitting}
            requireSku={false}
        />
    );
}
